import { Component, OnDestroy, OnInit } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { SiteMembership } from '@se-po/shared-data-access-models'
import {
  AnalyticsService,
  HouseholdProfilesService,
  SiteMembershipService,
  SurveyResultService,
} from '@se-po/shared-data-access-services'
import { CommerceOrder, OrderService } from '@se-po/se-my-sportsengine-data-access'
import {
  OpenRegistrationFilterAction,
  OpenRegistrationFilterKey,
  OpenRegistrationFilterOption,
  OpenRegistrationFilters,
} from '@se-po/se-registration-ui-feature-open-registration-list'
import { RegistrationCardTranslationsService } from '@se-po/se-registration-ui-registration-card'
import { DateTime } from 'luxon'
import { catchError, forkJoin, from, map, mergeMap, Observable, of, Subscription, switchMap, toArray } from 'rxjs'
import { SeFePaginationChange, SeFePaginationOptions } from 'se-fe-pagination'
import { SeFeTranslationsLoader } from 'se-fe-translations'
import { ApiResponse } from 'se-resource-types/dist/lib/common'
import { SurveyResult } from 'se-resource-types/dist/lib/Ngin'

interface CompletedRegistrationPersona {
  id?: number | string
  name?: string
  first_name?: string
  last_name?: string
  avatar_url?: string
  image_url?: string
  profile_image_url?: string
  profile_images?: CompletedRegistrationProfileImage[] | null
}

interface CompletedRegistrationProfileImage {
  image_type?: string
  url?: string
}

interface CompletedRegistrationHouseholdProfile {
  persona?: CompletedRegistrationPersona
}

interface CompletedRegistrationHouseholdProfilesResult {
  user?: {
    id?: number | string
  }
  profiles?: CompletedRegistrationHouseholdProfile[]
}

interface CompletedRegistrationSurveyResult extends SurveyResult {
  sport?: string
  sport_key?: string
  signup_types?: string | string[]
  persona?: CompletedRegistrationPersona
}

interface CompletedRegistrationSiteMembership extends Omit<SiteMembership, 'organization_logo' | 'site_logo_graphic'> {
  organization_logo: CompletedRegistrationOrganizationLogo
  site_logo_graphic: CompletedRegistrationSiteLogo | null
}

interface CompletedRegistrationOrganizationLogo {
  medium_logo_url: string
  url?: string
  large_logo_url?: string
  small_logo_url?: string
  tiny_logo_url?: string
}

interface CompletedRegistrationSiteLogo {
  medium_icon_url?: string
  small_icon_url?: string
  url?: string
  large_url?: string
  tiny_icon_url?: string
}

interface CompletedRegistrationCard {
  id: string
  registrationName: string
  orgName: string
  orgLogoUrl: string
  orgColor: string
  sport: string
  type: string
  avatarUrl: string
  backgroundImgUrl: string
  completedDate: string
  cardRouterLink: string | unknown[]
  cardRouterQueryParams: Record<string, string | number>
  orderRouterLink: string | unknown[]
  orderRouterQueryParams: Record<string, string | number>
  showShareAction: boolean
}

interface CompletedRegistrationFilterOptions {
  sport: OpenRegistrationFilterOption
  organization: OpenRegistrationFilterOption
  type: OpenRegistrationFilterOption[]
  person: OpenRegistrationFilterOption
}

interface CompletedRegistrationRecord {
  surveyResult: CompletedRegistrationSurveyResult
  filterOptions: CompletedRegistrationFilterOptions
  card: CompletedRegistrationCard
}

type CompletedRegistrationMineParams = Record<string, string | number>

@Component({
  selector: 'se-po-completed-registration-list',
  templateUrl: './completed-registration-list.component.html',
  styleUrls: ['./completed-registration-list.component.scss'],
  standalone: false,
})
export class CompletedRegistrationListComponent implements OnInit, OnDestroy {
  public loaded = false
  public error = false
  public noResults = false
  public surveyResults: CompletedRegistrationSurveyResult[] = []
  public completedRegistrationRecords: CompletedRegistrationRecord[] = []
  public filteredCards: CompletedRegistrationCard[] = []
  public paginatedCards: CompletedRegistrationCard[] = []
  public sports: OpenRegistrationFilterOption[] = []
  public organizations: OpenRegistrationFilterOption[] = []
  public signupTypeList: OpenRegistrationFilterOption[] = []
  public people: OpenRegistrationFilterOption[] = []
  public searchTerm = ''
  public filters: OpenRegistrationFilters = this.emptyFilters()

  private readonly defaultPageSize = 25
  private readonly pageSizeOptions = [25, 50, 100]
  private readonly commerceOrderFetchConcurrency = 5

  public paginationOptions: SeFePaginationOptions = {
    label: 'Registrations',
    currentPage: 1,
    total: 0,
    limit: this.defaultPageSize,
    limits: [...this.pageSizeOptions],
  }
  public readonly translateKey = 'SE_PO_SE_REGISTRATION_UI_FEATURE_COMPLETED_REGISTRATION_LIST.'

  private readonly subscriptions = new Subscription()
  private readonly translationsPath = 'se-registration-ui/feature-completed-registration-list/assets/translations'
  private readonly membershipByOrganizationId = new Map<string, CompletedRegistrationSiteMembership>()
  private readonly membershipBySiteId = new Map<string, CompletedRegistrationSiteMembership>()
  private readonly profilePersonaById = new Map<string, CompletedRegistrationPersona>()
  private readonly commerceOrderBySaleNumber = new Map<string, CommerceOrder>()
  private resultsSubscription?: Subscription
  private initialPersonFilterIds: string[] = []
  private initialPersonFiltersApplied = false

  constructor(
    private surveyResultService: SurveyResultService,
    private siteMembershipService: SiteMembershipService,
    private householdProfilesService: HouseholdProfilesService,
    private orderService: OrderService,
    private translationsLoader: SeFeTranslationsLoader,
    private registrationCardTranslations: RegistrationCardTranslationsService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private analyticsService: AnalyticsService,
  ) {}

  public ngOnInit(): void {
    this.initialPersonFilterIds = this.personFilterIdsFromRoute()
    this.applyInitialPersonFilters()
    void this.loadTranslations()
    this.loadCompletedRegistrations()
  }

  private async loadTranslations(): Promise<void> {
    try {
      await Promise.all([
        this.translationsLoader.load(this.translationsPath),
        this.registrationCardTranslations.load(),
      ])
    } catch {
      // Continue rendering with translation keys as a fallback if the asset request fails.
    }
  }

  public ngOnDestroy(): void {
    this.resultsSubscription?.unsubscribe()
    this.subscriptions.unsubscribe()
  }

  public handleSearchTermChange(searchTerm: string): void {
    this.trackCompletedRegistrationsClick('CompletedRegistrations-Search')
    this.searchTerm = searchTerm || ''
    this.loadVisibleCompletedRegistrations(1)
  }

  public handleFiltersChange(filters: OpenRegistrationFilters): void {
    const nextFilters = {
      sport: [...(filters?.sport || [])],
      organization: [...(filters?.organization || [])],
      type: [...(filters?.type || [])],
      person: [...(filters?.person || [])],
    }

    this.filters = {
      ...nextFilters,
    }
    this.loadVisibleCompletedRegistrations(1)
  }

  public handleFilterAction(action: OpenRegistrationFilterAction): void {
    this.trackCompletedRegistrationsFilterAction(action.filterKey)
  }

  public clearFilters(): void {
    this.trackChangedFilters(this.emptyFilters())
    this.searchTerm = ''
    this.filters = this.emptyFilters()
    this.loadVisibleCompletedRegistrations(1)
  }

  public handlePaginationChange(event: SeFePaginationChange): void {
    this.loadVisibleCompletedRegistrations(event.currentPage, event.limit)
  }

  private loadCompletedRegistrations(): void {
    this.resultsSubscription?.unsubscribe()
    this.loaded = false
    this.error = false
    this.noResults = false
    this.surveyResults = []
    this.completedRegistrationRecords = []
    this.filteredCards = []
    this.paginatedCards = []
    this.commerceOrderBySaleNumber.clear()

    this.resultsSubscription = forkJoin({
      surveyResults: this.surveyResultService.findMine({ page: 1, per_page: 50 }),
      visibleSurveyResults: this.surveyResultService.findMineResponse(
        this.completedRegistrationMineParams()
      ),
      siteMemberships: this.siteMembershipService.mine().pipe(
        catchError(() => of([] as SiteMembership[]))
      ),
      householdProfiles: this.householdProfilesService.mine().pipe(
        catchError(() => of({ profiles: [] } as CompletedRegistrationHouseholdProfilesResult))
      ),
    }).pipe(
      switchMap(({ surveyResults, visibleSurveyResults, siteMemberships, householdProfiles }) => (
        this.ensureCommerceOrdersForSurveyResults(
          (visibleSurveyResults.result || []) as CompletedRegistrationSurveyResult[]
        ).pipe(
          map(() => ({
            surveyResults,
            visibleSurveyResults,
            siteMemberships,
            householdProfiles,
          }))
        )
      ))
    ).subscribe({
      next: ({
        surveyResults,
        visibleSurveyResults,
        siteMemberships,
        householdProfiles,
      }) => {
        this.surveyResults = surveyResults as CompletedRegistrationSurveyResult[]
        this.setMemberships(siteMemberships)
        this.setProfilePersonas(householdProfiles as CompletedRegistrationHouseholdProfilesResult)
        this.completedRegistrationRecords = this.surveyResults.map((surveyResult) => (
          this.recordFromSurveyResult(surveyResult)
        ))
        this.setFilterOptions()
        this.setVisibleSurveyResults(visibleSurveyResults)
        this.loaded = true
      },
      error: () => {
        this.handleCompletedRegistrationsError()
      },
    })
    // resultsSubscription is managed explicitly (unsubscribed/replaced and cleaned up in ngOnDestroy).
  }

  private loadVisibleCompletedRegistrations(
    currentPage = this.paginationOptions.currentPage || 1,
    limit = this.paginationOptions.limit || this.defaultPageSize
  ): void {
    if (!this.loaded && !this.error && this.completedRegistrationRecords.length === 0) {
      this.loadCompletedRegistrations()
      return
    }

    this.resultsSubscription?.unsubscribe()
    this.loaded = false
    this.error = false
    this.noResults = false
    this.filteredCards = []
    this.paginatedCards = []

    const params = this.completedRegistrationMineParams(currentPage, limit)

    this.resultsSubscription = this.surveyResultService.findMineResponse(params).pipe(
      switchMap((surveyResultsResponse) => (
        this.ensureCommerceOrdersForSurveyResults(
          (surveyResultsResponse.result || []) as CompletedRegistrationSurveyResult[]
        ).pipe(
          map(() => surveyResultsResponse)
        )
      ))
    ).subscribe({
      next: (surveyResultsResponse) => {
        this.setVisibleSurveyResults(surveyResultsResponse, Number(params.page), Number(params.per_page))
        this.loaded = true
      },
      error: () => {
        this.handleVisibleCompletedRegistrationsError()
      },
    })
    // resultsSubscription is managed explicitly (unsubscribed/replaced and cleaned up in ngOnDestroy).
  }

  private handleCompletedRegistrationsError(): void {
    this.surveyResults = []
    this.completedRegistrationRecords = []
    this.filteredCards = []
    this.paginatedCards = []
    this.sports = []
    this.organizations = []
    this.signupTypeList = []
    this.people = []
    this.membershipByOrganizationId.clear()
    this.membershipBySiteId.clear()
    this.profilePersonaById.clear()
    this.error = true
    this.noResults = false
    this.loaded = true
  }

  private handleVisibleCompletedRegistrationsError(): void {
    this.filteredCards = []
    this.paginatedCards = []
    this.paginationOptions = {
      ...this.paginationOptions,
      total: 0,
    }
    this.error = true
    this.noResults = false
    this.loaded = true
  }

  private setVisibleSurveyResults(
    surveyResultsResponse: ApiResponse<SurveyResult[]>,
    requestedPage = this.paginationOptions.currentPage || 1,
    requestedLimit = this.paginationOptions.limit || this.defaultPageSize
  ): void {
    const visibleSurveyResults = (surveyResultsResponse.result || []) as CompletedRegistrationSurveyResult[]
    const visibleRecords = visibleSurveyResults.map((surveyResult) => this.recordFromSurveyResult(surveyResult))

    this.filteredCards = visibleRecords.map((record) => record.card)
    this.paginatedCards = this.filteredCards
    this.updatePagination(surveyResultsResponse, requestedPage, requestedLimit)
    this.noResults = !this.error && this.surveyResults.length > 0 && this.filteredCards.length === 0
  }

  private completedRegistrationMineParams(
    currentPage = this.paginationOptions.currentPage || 1,
    limit = this.paginationOptions.limit || this.defaultPageSize
  ): CompletedRegistrationMineParams {
    const pageSize = this.pageSizeOptions.includes(limit) ? limit : this.defaultPageSize
    const params: CompletedRegistrationMineParams = {
      page: Math.max(currentPage, 1),
      per_page: pageSize,
    }
    const search = this.searchTerm.trim()

    if (search) params.search = search
    this.addCommaSeparatedParam(params, 'sports', this.filters.sport)
    this.addCommaSeparatedParam(params, 'organization_ids', this.filters.organization)
    this.addCommaSeparatedParam(params, 'signup_types', this.filters.type)
    this.addCommaSeparatedParam(params, 'persona_ids', this.filters.person)

    return params
  }

  private addCommaSeparatedParam(
    params: CompletedRegistrationMineParams,
    key: 'sports' | 'organization_ids' | 'signup_types' | 'persona_ids',
    values: string[] = []
  ): void {
    const joinedValues = values
      .map((value) => value.trim())
      .filter(Boolean)
      .join(',')

    if (joinedValues) params[key] = joinedValues
  }

  private updatePagination(
    surveyResultsResponse: ApiResponse<SurveyResult[]>,
    currentPage = this.paginationOptions.currentPage || 1,
    limit = this.paginationOptions.limit || this.defaultPageSize
  ): void {
    const pagination = surveyResultsResponse.metadata?.pagination
    const pageSize = this.pageSizeOptions.includes(limit) ? limit : this.defaultPageSize
    const total = pagination?.total ?? this.filteredCards.length
    const page = Math.max(pagination?.current_page ?? currentPage, 1)

    this.paginationOptions = {
      label: 'Registrations',
      currentPage: page,
      total,
      limit: pageSize,
      limits: [...this.pageSizeOptions],
    }
  }

  private setMemberships(siteMemberships: SiteMembership[]): void {
    this.membershipByOrganizationId.clear()
    this.membershipBySiteId.clear()
    siteMemberships.forEach((membership) => {
      const completedMembership = membership as CompletedRegistrationSiteMembership

      this.membershipByOrganizationId.set(this.toId(completedMembership.organization_id), completedMembership)
      this.membershipBySiteId.set(this.toId(completedMembership.site_id), completedMembership)
    })
  }

  private setProfilePersonas(householdProfiles: CompletedRegistrationHouseholdProfilesResult): void {
    this.profilePersonaById.clear()
    const profiles = householdProfiles.profiles || []

    profiles.forEach((profile) => {
      if (!profile.persona?.id) return

      this.profilePersonaById.set(this.toId(profile.persona.id), profile.persona)
    })
  }

  private setCommerceOrders(commerceOrders: CommerceOrder[]): void {
    commerceOrders.forEach((commerceOrder) => {
      const saleNumber = this.toId(commerceOrder.sale_number)
      if (!saleNumber) return

      this.commerceOrderBySaleNumber.set(saleNumber, commerceOrder)
    })
  }

  private ensureCommerceOrdersForSurveyResults(
    surveyResults: CompletedRegistrationSurveyResult[]
  ): Observable<void> {
    return this.fetchCommerceOrders(surveyResults).pipe(
      map((commerceOrders) => {
        this.setCommerceOrders(commerceOrders)
      })
    )
  }

  private setFilterOptions(): void {
    this.sports = this.sortedUniqueOptions(
      this.completedRegistrationRecords.map((record) => record.filterOptions.sport)
    )
    this.organizations = this.sortedUniqueOptions(
      this.completedRegistrationRecords.map((record) => record.filterOptions.organization)
    )
    this.signupTypeList = this.sortedUniqueOptions(
      this.completedRegistrationRecords.flatMap((record) => record.filterOptions.type)
    )
    this.people = this.sortedUniqueOptions(
      this.completedRegistrationRecords.map((record) => record.filterOptions.person)
    )
  }

  private applyInitialPersonFilters(): void {
    if (this.initialPersonFiltersApplied) return

    this.initialPersonFiltersApplied = true
    if (!this.initialPersonFilterIds.length) return

    this.filters = {
      ...this.filters,
      person: [...this.initialPersonFilterIds],
    }
  }

  private personFilterIdsFromRoute(): string[] {
    return this.personFilterIds(
      this.activatedRoute.snapshot.queryParamMap.get('personFilters')
    )
  }

  private personFilterIds(personFilters: string | null): string[] {
    return (personFilters || '')
      .split(',')
      .map((personFilter) => personFilter.trim())
      .filter(Boolean)
  }

  private sortedUniqueOptions(options: OpenRegistrationFilterOption[]): OpenRegistrationFilterOption[] {
    const optionsById = new Map<string, string>()

    options.forEach(({ id, name }) => {
      if (!id || optionsById.has(id)) return

      optionsById.set(id, name || id)
    })

    return Array.from(optionsById.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((left, right) => left.name.localeCompare(right.name, undefined, { sensitivity: 'base' }))
  }

  private trackChangedFilters(nextFilters: OpenRegistrationFilters): void {
    if (this.filterValuesChanged(this.filters.sport, nextFilters.sport)) {
      this.trackCompletedRegistrationsFilterAction('sport')
    }

    if (this.filterValuesChanged(this.filters.organization, nextFilters.organization)) {
      this.trackCompletedRegistrationsFilterAction('organization')
    }

    if (this.filterValuesChanged(this.filters.type, nextFilters.type)) {
      this.trackCompletedRegistrationsFilterAction('type')
    }

    if (this.filterValuesChanged(this.filters.person || [], nextFilters.person || [])) {
      this.trackCompletedRegistrationsFilterAction('person')
    }
  }

  private trackCompletedRegistrationsFilterAction(filterKey: OpenRegistrationFilterKey): void {
    const actionByFilterKey: Record<OpenRegistrationFilterKey, string> = {
      organization: 'CompletedRegistrations-Filter-Organization',
      person: 'CompletedRegistrations-Filter-Person',
      sport: 'CompletedRegistrations-Filter-Sport',
      type: 'CompletedRegistrations-Filter-Type',
    }

    this.trackCompletedRegistrationsClick(actionByFilterKey[filterKey])
  }

  private filterValuesChanged(previousValues: string[] = [], nextValues: string[] = []): boolean {
    return previousValues.length !== nextValues.length ||
      previousValues.some((value, index) => value !== nextValues[index])
  }

  private trackCompletedRegistrationsClick(action: string): void {
    this.analyticsService.seEvent(action)
  }

  private recordFromSurveyResult(surveyResult: CompletedRegistrationSurveyResult): CompletedRegistrationRecord {
    const filterOptions = this.filterOptionsFromSurveyResult(surveyResult)

    return {
      surveyResult,
      filterOptions,
      card: this.cardFromSurveyResult(surveyResult, filterOptions),
    }
  }

  private filterOptionsFromSurveyResult(
    surveyResult: CompletedRegistrationSurveyResult
  ): CompletedRegistrationFilterOptions {
    return {
      sport: {
        id: this.sportId(surveyResult),
        name: this.sportLabel(surveyResult),
      },
      organization: {
        id: this.organizationId(surveyResult),
        name: this.organizationName(surveyResult),
      },
      type: this.typeOptions(surveyResult),
      person: {
        id: this.personId(surveyResult),
        name: this.personLabel(surveyResult),
      },
    }
  }

  private cardFromSurveyResult(
    surveyResult: CompletedRegistrationSurveyResult,
    filterOptions: CompletedRegistrationFilterOptions
  ): CompletedRegistrationCard {
    return {
      id: this.toId(surveyResult.id),
      registrationName: surveyResult.survey_name || '',
      orgName: filterOptions.organization.name,
      orgLogoUrl: this.organizationLogoUrl(surveyResult),
      orgColor: this.organizationColor(surveyResult),
      sport: filterOptions.sport.name,
      type: this.typeLabel(surveyResult),
      avatarUrl: this.avatarUrl(surveyResult),
      backgroundImgUrl: this.organizationLogoUrl(surveyResult),
      completedDate: this.completedDate(surveyResult.placed_at),
      cardRouterLink: this.registrationDetailsRoute(surveyResult),
      cardRouterQueryParams: this.registrationDetailsQueryParams(surveyResult),
      orderRouterLink: this.orderDetailsRoute(surveyResult),
      orderRouterQueryParams: this.orderDetailsQueryParams(surveyResult),
      showShareAction: false,
    }
  }

  private sportId(surveyResult: CompletedRegistrationSurveyResult): string {
    return this.toId(surveyResult.sport_key || surveyResult.sport)
  }

  private sportLabel(surveyResult: CompletedRegistrationSurveyResult): string {
    return surveyResult.sport || this.formatDisplayName(surveyResult.sport_key)
  }

  private organizationId(surveyResult: CompletedRegistrationSurveyResult): string {
    return this.toId(surveyResult.organization_id)
  }

  private organizationName(surveyResult: CompletedRegistrationSurveyResult): string {
    const membership = this.membershipForSurveyResult(surveyResult)

    return membership?.organization_name || surveyResult.organization_name || surveyResult.site_name || ''
  }

  private organizationLogoUrl(surveyResult: CompletedRegistrationSurveyResult): string {
    const membership = this.membershipForSurveyResult(surveyResult)
    const organizationLogo = membership?.organization_logo
    const siteLogo = membership?.site_logo_graphic

    return organizationLogo?.medium_logo_url ||
      organizationLogo?.small_logo_url ||
      organizationLogo?.url ||
      organizationLogo?.large_logo_url ||
      organizationLogo?.tiny_logo_url ||
      siteLogo?.medium_icon_url ||
      siteLogo?.small_icon_url ||
      siteLogo?.url ||
      siteLogo?.large_url ||
      siteLogo?.tiny_icon_url ||
      ''
  }

  private organizationColor(surveyResult: CompletedRegistrationSurveyResult): string {
    const membership = this.membershipForSurveyResult(surveyResult)
    return this.normalizeColor(membership?.organization_color)
  }

  private membershipForSurveyResult(
    surveyResult: CompletedRegistrationSurveyResult
  ): CompletedRegistrationSiteMembership | undefined {
    return this.membershipBySiteId.get(this.toId(surveyResult.site_id)) ||
      this.membershipByOrganizationId.get(this.organizationId(surveyResult))
  }

  private typeId(surveyResult: CompletedRegistrationSurveyResult): string {
    return this.toId(surveyResult.survey_type)
  }

  private typeLabel(surveyResult: CompletedRegistrationSurveyResult): string {
    const signupTypes = this.signupTypes(surveyResult)
    if (signupTypes.length) return signupTypes.join(', ')

    return this.formatDisplayName(this.typeId(surveyResult))
  }

  private typeOptions(surveyResult: CompletedRegistrationSurveyResult): OpenRegistrationFilterOption[] {
    const signupTypes = this.signupTypes(surveyResult)

    if (signupTypes.length) {
      return signupTypes.map((signupType) => ({
        id: signupType,
        name: signupType,
      }))
    }

    return [{
      id: this.typeId(surveyResult),
      name: this.typeLabel(surveyResult),
    }]
  }

  private signupTypes(surveyResult: CompletedRegistrationSurveyResult): string[] {
    const signupTypes = surveyResult.signup_types

    if (Array.isArray(signupTypes)) {
      return signupTypes.map((signupType) => signupType.trim()).filter(Boolean)
    }

    return this.toId(signupTypes)
      .split(',')
      .map((signupType) => signupType.trim())
      .filter(Boolean)
  }

  private personId(surveyResult: CompletedRegistrationSurveyResult): string {
    return this.toId(surveyResult.persona?.id ?? surveyResult.persona_id)
  }

  private personLabel(surveyResult: CompletedRegistrationSurveyResult): string {
    const persona = surveyResult.persona
    const profilePersona = this.profilePersonaById.get(this.personId(surveyResult))

    return this.personaDisplayName(persona) ||
      this.personaDisplayName(profilePersona) ||
      this.personId(surveyResult)
  }

  private personaDisplayName(persona?: CompletedRegistrationPersona): string {
    if (!persona) return ''

    const fullName = [persona.first_name, persona.last_name]
      .filter(Boolean)
      .join(' ')

    return persona.name || fullName
  }

  private avatarUrl(surveyResult: CompletedRegistrationSurveyResult): string {
    const persona = surveyResult.persona
    const profilePersona = this.profilePersonaById.get(this.personId(surveyResult))

    const imageUrl = persona?.avatar_url ||
      persona?.image_url ||
      persona?.profile_image_url ||
      this.profileImageUrl(persona?.profile_images) ||
      this.profileImageUrl(profilePersona?.profile_images) ||
      ''

    return imageUrl || this.initialsAvatarUrl(this.personaInitials(persona, profilePersona))
  }

  private profileImageUrl(profileImages?: CompletedRegistrationProfileImage[] | null): string {
    if (!Array.isArray(profileImages)) return ''

    const imageTypePriority: Record<string, number> = {
      crop_icon: 0,
      crop: 1,
      original: 2,
    }
    let selectedImageUrl = ''
    let selectedPriority = Number.POSITIVE_INFINITY

    for (const { image_type, url } of profileImages) {
      const priority = image_type ? imageTypePriority[image_type] : undefined
      if (priority === undefined || !url || priority >= selectedPriority) continue

      selectedImageUrl = url
      selectedPriority = priority
      if (selectedPriority === imageTypePriority.crop_icon) break
    }

    return selectedImageUrl
  }

  private personaInitials(
    persona?: CompletedRegistrationPersona,
    profilePersona?: CompletedRegistrationPersona
  ): string {
    const sourcePersona = this.personaWithNameParts(persona) || this.personaWithNameParts(profilePersona)
    if (!sourcePersona) return ''

    const firstName = sourcePersona.first_name?.trim()
    const lastName = sourcePersona.last_name?.trim()

    if (firstName || lastName) {
      return [firstName, lastName]
        .filter(Boolean)
        .map((name) => name?.charAt(0)?.toUpperCase())
        .join('')
    }

    return (sourcePersona.name || '')
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((name) => name.charAt(0).toUpperCase())
      .join('')
  }

  private personaWithNameParts(persona?: CompletedRegistrationPersona): CompletedRegistrationPersona | undefined {
    if (!persona) return undefined

    return persona.first_name || persona.last_name || persona.name ? persona : undefined
  }

  private initialsAvatarUrl(initials: string): string {
    if (!initials) return ''

    const escapedInitials = this.escapeSvgText(initials)
    const svg = [
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 154 154">',
      '<circle cx="77" cy="77" r="77" fill="#1272af"/>',
      '<text x="77" y="84" text-anchor="middle" dominant-baseline="middle" fill="#fff"',
      ` font-family="Figtree, Arial, sans-serif" font-size="52" font-weight="600">${escapedInitials}</text>`,
      '</svg>',
    ].join('')

    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`
  }

  private escapeSvgText(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
  }

  private completedDate(placedAt?: string): string {
    const completedAt = placedAt ? DateTime.fromISO(placedAt) : null

    return completedAt?.isValid ? completedAt.toFormat('LLLL d, yyyy') : ''
  }

  private registrationDetailsRoute(surveyResult: CompletedRegistrationSurveyResult): string | unknown[] {
    const registrationId = this.toId(surveyResult.id)
    if (!registrationId) return ''

    return `/user/registrations/${encodeURIComponent(registrationId)}`
  }

  private registrationDetailsQueryParams(
    surveyResult: CompletedRegistrationSurveyResult
  ): Record<string, string | number> {
    const siteId = this.toId(surveyResult.site_id)

    return siteId ? { siteId } : {}
  }

  private orderDetailsRoute(surveyResult: CompletedRegistrationSurveyResult): string | unknown[] {
    const orderId = this.toId(surveyResult.order_id)
    if (orderId) return '/user/orders'

    const saleNumber = this.toId(surveyResult.order_reference?.sale_number)
    if (!saleNumber || !this.commerceOrderBySaleNumber.has(saleNumber)) return ''

    return `/user/orders/${encodeURIComponent(saleNumber)}`
  }

  private orderDetailsQueryParams(
    surveyResult: CompletedRegistrationSurveyResult
  ): Record<string, string | number> {
    const orderId = this.toId(surveyResult.order_id)
    const returnTo = this.router.url

    return orderId
      ? { url: `/order/show_user/${orderId}`, returnTo }
      : { returnTo }
  }

  private fetchCommerceOrders(
    surveyResults: CompletedRegistrationSurveyResult[]
  ): Observable<CommerceOrder[]> {
    const saleNumbers = Array.from(new Set(
      surveyResults
        .map((surveyResult) => this.toId(surveyResult.order_reference?.sale_number))
        .filter((saleNumber) => Boolean(saleNumber) && !this.commerceOrderBySaleNumber.has(saleNumber))
    ))

    if (!saleNumbers.length) {
      return of([] as CommerceOrder[])
    }

    return from(saleNumbers).pipe(
      mergeMap((saleNumber) => (
        // Orders must be fetched individually because the API does not support batch requests for non-platform users
        this.orderService.find(saleNumber).pipe(
          map((order) => order as CommerceOrder),
          catchError(() => of(null))
        )
      ), this.commerceOrderFetchConcurrency),
      toArray(),
      map((orders) => orders.filter((order): order is CommerceOrder => Boolean(order)))
    )
  }

  private formatDisplayName(value?: string): string {
    return this.toId(value)
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .split(' ')
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ')
  }

  private normalizeColor(color?: string): string {
    const normalizedColor = (color || '').trim()
    if (!normalizedColor) return ''
    if (normalizedColor.startsWith('#') || normalizedColor.startsWith('rgb')) return normalizedColor

    return `#${normalizedColor}`
  }

  private toId(value?: number | string): string {
    return value === undefined || value === null ? '' : String(value)
  }

  private emptyFilters(): OpenRegistrationFilters {
    return {
      sport: [],
      organization: [],
      type: [],
      person: [],
    }
  }
}
